import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Box, Typography, CircularProgress, TableCell, Tooltip } from '@mui/material';

// api
import { useAppSelector } from '../../hooks/hooks';
import TermApi from '../../api/term.api';
import TermListingApi from '../../api/term-listing.api';

// comps
import DataGrid from '../../components/grid/dataGrid';
import ContentContainer from '../../components/layout/contentContainer';
import { CustomTableInputCell } from '../../components/grid/CustomTableCell';
import FilterContainer from '../../components/layout/filterContainer';
import GlobalDropdown from '../../components/global/dropdown';
import GlobalInput from '../../components/global/input';
import GlobalButton from '../../components/global/button';

// icons
import { ReactComponent as EmptyTimeIcon } from '../../assets/icons/empty-time-icon.svg';
import { ReactComponent as EmptyDateIcon } from '../../assets/icons/empty-date-icon.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';

// constants
import { colors } from '../../theme';
import { MOMENT_DATE_DISPLAY_FORMAT, MOMENT_DATE_SIMPLE_FORMAT } from '../../constants/date_format';

const GENRSList = (props: any) => {
    const appState = useAppSelector((state) => state.appState);
    const _termApi = new TermApi();
    const _termListingApi = new TermListingApi();

    const { campus, term } = appState;

    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState({
        data: [],
        termCampuses: [],
        filterObject: {
            subject: '',
            comment: '',
        },
        isClearAll: false,
        defaultTerm: {},
    });
    const [resetPage, setResetPage] = useState(false);
    const [selected, setSelected] = useState({
        termCampus: null,
    });
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [activeRow, setActiveRow] = useState({});

    const loadData = async (termCampusId: number) => {
        setIsLoading(true);
        const genrData = await _termListingApi.GetGENRList(termCampusId);

        setState({ ...state, data: genrData });
    };

    const loadTermCampuses = async (campusId: number) => {
        const terms = await _termApi.GetTermsByCampusId(campusId);
        // TODO: Return all terms in descending order (Below is a front-end hack)
        const termCampuses = terms.reverse();
        const termCampus = terms?.find((term) => term.isDefault);

        setState({
            ...state,
            termCampuses,
            defaultTerm: termCampus,
        });

        setSelected({
            ...selected,
            termCampus: termCampus,
        });
    };

    useEffect(() => {
        if (selected.termCampus) {
            loadData(selected.termCampus.termCampusId)
                .catch((error) => toast.error('Error loading GENRS data'))
                .finally(() => setIsLoading(false));
        }
    }, [selected.termCampus]);

    useEffect(() => {
        loadTermCampuses(campus?.id).catch((error) => toast.error("Error loading data"));
    }, [campus]);

    const handleTermChange = (name: any, value: any) => {
        const termCampus = value;
        setSelected({ ...selected, termCampus });
    };

    const handleFilter = (name: any, value: any) => {
        setState({
            ...state,
            filterObject: {
                ...state.filterObject,
                [name]: name === 'subject' ? value.toUpperCase() : value,
            },
        });
        setButtonDisabled(false);
    };

    const handleClearAll = () => {
        let filterObject = { ...state.filterObject };
        Object.keys(filterObject).forEach((i) => (filterObject[i] = ''));

        setState({
            ...state,
            filterObject,
            isClearAll: true,
        });

        setSelected({ ...selected, termCampus: state.defaultTerm });
        setResetPage(true);
        setButtonDisabled(true);
    };

    useEffect(() => {
        if (!Object.values(state.filterObject).every((o) => o === '')) {
            setResetPage(true);
        } else {
            setResetPage(false);
            setButtonDisabled(true);
        }
    }, [state.filterObject]);

    useEffect(() => {
        setState({ ...state, isClearAll: false });
    }, [state.isClearAll]);

    const getGridData = () => {
        let genrsData = [...state.data];
        const { subject, comment } = state.filterObject;

        if (subject) genrsData = genrsData.filter((item) => item.course?.subjectCode.includes(subject));
        if (comment) genrsData = genrsData.filter((item) => item.note?.toLowerCase().includes(comment.toLowerCase()));

        return genrsData;
    };

    const handleUpdateComment = async (dataItem: any) : Promise<boolean> => {
        try {
            const { course } = dataItem;
            await _termListingApi.UpdateSectionComment(dataItem);
            toast.success(`Comment for ${course.subjectCode} - ${dataItem.crn} has been successfully saved.`);
            return true;
        } catch (error) {
            toast.error("Error updating comment");
            return false;
        }
    };

    const renderGENRSGrid = (data: any, onChange: any, handleInputFocus: any) => {
        const { course, crn, room, startDate, endDate, timeIn, timeOut, days, enrolled, capacity, school, note } = data;

        return (
            <>
                <TableCell sx={{ width: '5%' }}>{course.subjectCode}</TableCell>
                <TableCell sx={{ width: '6%' }}>{course.number}</TableCell>
                <TableCell>
                    <DetailedTableCell>
                        <Tooltip title={course.title} arrow>
                            <Typography variant="body2" sx={{ lineHeight: 1.15 }}>
                                <span>{crn}</span>
                                <InfoIcon />
                            </Typography>
                        </Tooltip>
                    </DetailedTableCell>
                    {/* {crn} */}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{room}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                    {startDate && endDate ? (
                        <>
                            {moment(startDate).format(MOMENT_DATE_DISPLAY_FORMAT)} - {moment(endDate).format(MOMENT_DATE_DISPLAY_FORMAT)}
                        </>
                    ) : (
                        <DateTimeTableCell>
                            <EmptyDateIcon />
                        </DateTimeTableCell>
                    )}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                    {timeIn && timeOut ? (
                        <>
                            {timeIn} - {timeOut}
                        </>
                    ) : (
                        <DateTimeTableCell>
                            <EmptyTimeIcon />
                        </DateTimeTableCell>
                    )}
                </TableCell>
                <TableCell sx={{ width: '5%', textAlign: 'center' }}>{days}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                    {enrolled}/{capacity}
                </TableCell>
                <TableCell>{school}</TableCell>
                <CustomTableInputCell
                    {...{
                        data,
                        name: 'note',
                        onChange: (name, value) => {
                            onChange(name, value, data, 'default');
                        },
                        isDefault: true,
                        placeholder: 'New Comment',
                        id: 'crn',
                        onFocus: (data) => {
                            handleInputFocus(data);
                        },
                        tooltipMsg: note,
                    }}
                />
            </>
        );
    };

    return (
        <>
            <FilterContainer sx={{ justifyContent: 'space-between' }}>
                <FlexBox>
                    <GlobalDropdown
                        label="Term"
                        value={selected.termCampus}
                        data={state.termCampuses}
                        placeholder={selected.termCampus ? selected.termCampus.termCode : 'Select a term'}
                        name="termCode"
                        field="termId"
                        width="150px"
                        dataItemKey={'term'}
                        onChange={handleTermChange}
                    />
                    <GlobalInput
                        label="filter"
                        icon={<FilterIcon />}
                        placeholder="Subject"
                        name={'subject'}
                        type="text"
                        value={state.filterObject.subject}
                        onChange={handleFilter}
                        sx={{ width: '85px' }}
                    />
                    <GlobalInput placeholder="Comment(s)" name={'comment'} type="text" value={state.filterObject.comment} onChange={handleFilter} sx={{ width: '160px', ml: '-5px' }} />
                </FlexBox>
                <FlexBox sx={{ width: '10%' }}>
                    <GlobalButton variant="round" text="clear all" fullWidth secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAll} disabled={buttonDisabled} />
                </FlexBox>
            </FilterContainer>
            {state.data?.length === 0 && isLoading ? (
                <ContentContainer padding={'0px 25px'}>
                    <FlexBox sx={{ justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={100} />
                    </FlexBox>
                </ContentContainer>
            ) : (
                <DataGrid
                    isEdit
                    isSave
                    isScrolling
                    clickable
                    topHeight={50}
                    data={getGridData()}
                    renderRows={renderGENRSGrid}
                    columns={gridColumns}
                    dataItemKey="rowId"
                    onSaveAsync={(dataItem: any) => handleUpdateComment(dataItem)}
                    padding={'5px'}
                    isClearSorting={state.isClearAll}
                    resetPage={resetPage}
                    onHeaderClick={() => {
                        setButtonDisabled(false);
                    }}
                    activeCell={activeRow}
                    onRowClick={(item: any) => {
                        setActiveRow(item);
                    }}
                />
            )}
        </>
    );
};

export default GENRSList;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;
        & > * {
            margin-right: 10px;
        }
    }
`;

const DetailedTableCell = styled.div`
    p {
        display: flex;
        align-items: center;

        svg {
            width: 14px;
            height: auto;
            margin-left: 3px;

            path {
                fill: ${colors.grey.medium};
            }
        }
    }
`;

const DateTimeTableCell = styled.div`
    svg {
        width: 20px;
        height: auto;

        path {
            fill: ${colors.grey.medium};
        }
    }
`;

const gridColumns = [
    {
        id: 'course',
        label: 'Subject',
        objectId: 'subjectCode',
    },
    {
        label: 'Course #',
        disabled: true,
    },
    {
        id: 'crn',
        label: 'CRN',
    },
    {
        id: 'room',
        label: 'Room',
        sx: {
            textAlign: 'center',
        },
    },
    {
        id: 'startDate',
        label: 'Date',
        sx: {
            textAlign: 'center',
        },
    },
    {
        id: 'timeIn',
        label: 'Time',
        sx: {
            textAlign: 'center',
        },
    },
    {
        label: 'Day(s)',
        sx: {
            textAlign: 'center',
        },
        disabled: true,
    },
    {
        id: 'enrolled',
        label: 'Enrol / Cap',
        sx: {
            textAlign: 'center',
        },
    },

    {
        label: 'School',
        disabled: true,
    },
    {
        label: 'Comment(s)',
        disabled: true,
    },
];
